import { Stack, Grid, IconButton, Typography, Box, Button } from '@mui/material';
import {
  useWatch,
  Controller,
  type Control,
  type FieldPath,
  type UseFieldArrayAppend,
  type UseFieldArrayRemove,
} from 'react-hook-form';
import type {
  AddSaleItemStockAllowanceType,
  SaleItemsFormValues,
} from './procedure-sales-form-dialog';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { FormattedMessage, useIntl } from 'react-intl';
import StockItemsAutoComplete from '../../components/StockItemsAutoComplete';
import { StockAllowance, mapStocksStockItemsToFlatStockItems } from '../../entity';
import { useStockItemsQuery } from '../../queries';
import TextField from '@shared/components/TextField';
import { useCurrency } from '@features/currency/currency.hooks';

type AddSaleItemsFormProps<TForm extends SaleItemsFormValues> = {
  control: Control<TForm>;
  name: FieldPath<TForm>;
  append: UseFieldArrayAppend<SaleItemsFormValues, 'stockAllowances'>;
  remove: UseFieldArrayRemove;
  fields: AddSaleItemStockAllowanceType;
  readonlyAllowances: (Omit<StockAllowance, 'id' | 'quantity'> & {
    stockAllowanceId: string;
    quantity: number | null;
    stockItemName?: string;
    avgUnitPrice?: string;
  })[];
};

const AddSaleItemsForm = (props: AddSaleItemsFormProps<SaleItemsFormValues>) => {
  const { control, append, remove, fields, readonlyAllowances } = props;
  const intl = useIntl();
  const { preferredCurrency } = useCurrency();
  const { data: stocksWithStockItems } = useStockItemsQuery(undefined);
  const stockItems = mapStocksStockItemsToFlatStockItems(stocksWithStockItems);
  const watchedStockAllowances = useWatch({ control });

  return (
    <Stack spacing={3}>
      {fields.map((field, index) => {
        const fielArrayStockItem =
          watchedStockAllowances.stockAllowances && watchedStockAllowances.stockAllowances[index];
        {
          const stockItem =
            fielArrayStockItem &&
            stockItems &&
            stockItems.find(stockItem => stockItem.id === fielArrayStockItem.stockItemId);

          const updatedStockQuantity = stockItem && stockItem.quantity;
          const stockItemQuantityCritical =
            updatedStockQuantity && updatedStockQuantity < stockItem.threshold;

          return (
            <Stack key={field.stockAllowanceId} spacing={0.5}>
              <Grid container spacing={2} alignItems="center">
                <Controller
                  name={`stockAllowances.${index}.stockAllowanceId`}
                  control={control}
                  render={() => <></>}
                />
                <Grid size={12}>
                  <StockItemsAutoComplete
                    fieldPath={`stockAllowances.${index}.stockItemId`}
                    control={control}
                    type="GOODS"
                  />
                </Grid>
                <Grid size={5}>
                  <TextField
                    type="number"
                    label={intl.formatMessage({
                      id: 'formDialogAddSaleItems.packageCount',
                      defaultMessage: 'Počet balení (ks)',
                    })}
                    fieldPath={`stockAllowances.${index}.quantity`}
                    control={control}
                    required
                  />
                </Grid>
                <Grid size={5}>
                  <TextField
                    type="number"
                    label={intl.formatMessage({
                      id: 'formDialogAddSaleItems.price',
                      defaultMessage: `Cena (${preferredCurrency})`,
                    })}
                    fieldPath={`stockAllowances.${index}.totalPrice`}
                    control={control}
                    required
                  />
                </Grid>
                <Grid size={2}>
                  <IconButton onClick={() => remove(index)} color="error">
                    <DeleteOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>

              {stockItem ? (
                <Stack spacing={0.5}>
                  <Typography color="text.secondary" fontSize="0.8rem" paddingLeft="0.2rem">
                    <FormattedMessage
                      id="formDialogAddSaleItems.stockState"
                      defaultMessage="Aktuálně ve skladu"
                    />
                    <Box
                      component="span"
                      color={stockItemQuantityCritical ? 'primary.main' : 'success.main'}
                      fontWeight="bold"
                    >
                      {` ${stockItem.packageCount} ks`}
                    </Box>
                    <Box component="span" color="text.secondary">
                      {` (${stockItem.lastPackageQuantity} ${stockItem.unit}/ks)`}
                    </Box>
                  </Typography>
                  {stockItem.retailPrice && (
                    <Typography color="text.secondary" fontSize="0.8rem" paddingLeft="0.2rem">
                      <FormattedMessage id="formDialogAddSaleItems.retailPrice" />
                      <Box component="span" color="info.main" fontWeight="bold">
                        {` ${stockItem.retailPrice} ${preferredCurrency}`}
                      </Box>
                    </Typography>
                  )}
                  {stockItem.brand && (
                    <Typography color="text.secondary" fontSize="0.8rem" paddingLeft="0.2rem">
                      <FormattedMessage
                        id="formDialogAddSaleItems.brand"
                        defaultMessage="Značka:"
                      />
                      <Box component="span" fontWeight="bold">
                        {` ${stockItem.brand}`}
                      </Box>
                    </Typography>
                  )}
                </Stack>
              ) : null}
            </Stack>
          );
        }
      })}
      {readonlyAllowances.map(stockAllowance => (
        <Stack key={stockAllowance.stockAllowanceId} spacing={0.5}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={5}>
              <TextField
                label={intl.formatMessage({
                  id: 'formDialogAddSaleItems.item',
                  defaultMessage: 'Položka',
                })}
                readonly
                defaultValue={stockAllowance.stockItemName}
                fullWidth
              />
            </Grid>
            <Grid size={2}>
              <TextField
                label={intl.formatMessage({
                  id: 'formDialogAddSaleItems.quantity',
                  defaultMessage: 'Množství',
                })}
                readonly
                defaultValue={stockAllowance.quantity}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                label={intl.formatMessage({
                  id: 'formDialogAddSaleItems.price',
                  defaultMessage: 'Cena',
                })}
                readonly
                defaultValue={stockAllowance.avgUnitPrice}
              />
            </Grid>
            <Grid size={2}>
              <IconButton color="error" disabled>
                <DeleteOutlineIcon />
              </IconButton>
            </Grid>
          </Grid>

          <Typography color="text.secondary" fontSize="0.8rem" paddingLeft="0.2rem">
            <FormattedMessage
              id="formDialogAddSaleItems.stockItemWarrning"
              defaultMessage="Položka je ze skladu, ke kterému již nemáte oprávnění nebo je smazána."
            />
          </Typography>
        </Stack>
      ))}
      <Button
        onClick={() =>
          append({
            stockItemId: '',
            quantity: null,
            stockAllowanceId: '',
            avgUnitPrice: undefined,
            type: 'SALE',
            totalPrice: 0,
          })
        }
        variant="outlined"
      >
        <FormattedMessage
          id="formDialogAddSaleItems.addItem"
          defaultMessage="Přidat zboží k prodeji"
        />
      </Button>
    </Stack>
  );
};

export default AddSaleItemsForm;
